// src/components/onboarding/SignatureModal.tsx
//
// Signature capture modal with two tabs:
//  - Draw:   reuse SignaturePad (canvas) -> PNG dataURL, method "DRAWN".
//  - Upload: pick an image (png/jpeg), auto-remove the (near-)white background
//            on a canvas, preview the transparent-bg result, method "UPLOADED".
// Returns { dataUrl, method } to the caller on confirm.

import React, { useRef, useState } from 'react';
import { X, PenLine, Upload, Loader2, Check, Info } from 'lucide-react';
import SignaturePad from './SignaturePad';
import { SignatureMethod } from '../../types';

interface SignatureModalProps {
    onClose: () => void;
    onConfirm: (result: { dataUrl: string; method: SignatureMethod }) => void;
}

type Tab = 'draw' | 'upload';

// Pixels whose r,g,b are all above this are treated as background and made transparent.
const WHITE_THRESHOLD = 230;

const SignatureModal: React.FC<SignatureModalProps> = ({ onClose, onConfirm }) => {
    const [tab, setTab] = useState<Tab>('draw');

    // Draw tab.
    const [drawnData, setDrawnData] = useState<string | null>(null);

    // Upload tab.
    const [uploadData, setUploadData] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Load the picked image into a canvas and knock out the near-white background.
    const handleFile = (file: File) => {
        setUploadError(null);
        setUploadData(null);
        if (!/image\/(png|jpeg)/.test(file.type)) {
            setUploadError('Please choose a PNG or JPEG image.');
            return;
        }
        setProcessing(true);
        const reader = new FileReader();
        reader.onerror = () => {
            setProcessing(false);
            setUploadError('Could not read the file. Please try another image.');
        };
        reader.onload = () => {
            const img = new Image();
            img.onerror = () => {
                setProcessing(false);
                setUploadError('Could not load the image. Please try another file.');
            };
            img.onload = () => {
                try {
                    // Cap the working size so large photos stay reasonable.
                    const maxW = 1000;
                    const scale = img.width > maxW ? maxW / img.width : 1;
                    const w = Math.max(1, Math.round(img.width * scale));
                    const h = Math.max(1, Math.round(img.height * scale));
                    const canvas = document.createElement('canvas');
                    canvas.width = w;
                    canvas.height = h;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        setProcessing(false);
                        setUploadError('Your browser could not process the image.');
                        return;
                    }
                    ctx.drawImage(img, 0, 0, w, h);
                    const imageData = ctx.getImageData(0, 0, w, h);
                    const data = imageData.data;
                    for (let i = 0; i < data.length; i += 4) {
                        const r = data[i];
                        const g = data[i + 1];
                        const b = data[i + 2];
                        if (r >= WHITE_THRESHOLD && g >= WHITE_THRESHOLD && b >= WHITE_THRESHOLD) {
                            data[i + 3] = 0; // transparent
                        }
                    }
                    ctx.putImageData(imageData, 0, 0);
                    setUploadData(canvas.toDataURL('image/png'));
                } catch {
                    setUploadError('Could not process the image. Please try another file.');
                } finally {
                    setProcessing(false);
                }
            };
            img.src = reader.result as string;
        };
        reader.readAsDataURL(file);
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
    };

    const currentData = tab === 'draw' ? drawnData : uploadData;

    const handleConfirm = () => {
        if (tab === 'draw' && drawnData) {
            onConfirm({ dataUrl: drawnData, method: 'DRAWN' });
        } else if (tab === 'upload' && uploadData) {
            onConfirm({ dataUrl: uploadData, method: 'UPLOADED' });
        }
    };

    const TabButton: React.FC<{ value: Tab; icon: React.ReactNode; label: string }> = ({ value, icon, label }) => (
        <button
            type="button"
            onClick={() => setTab(value)}
            className={`flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all ${
                tab === value ? 'bg-brand text-white shadow-md' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
        >
            {icon} {label}
        </button>
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                    <h3 className="font-serif font-bold text-xl text-primary">Add your signature</h3>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto">
                    <div className="flex gap-2 mb-6">
                        <TabButton value="draw" icon={<PenLine className="w-4 h-4" />} label="Draw" />
                        <TabButton value="upload" icon={<Upload className="w-4 h-4" />} label="Upload" />
                    </div>

                    {tab === 'draw' && (
                        <SignaturePad onChange={setDrawnData} />
                    )}

                    {tab === 'upload' && (
                        <div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg"
                                onChange={onFileChange}
                                className="hidden"
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 py-8 text-gray-500 hover:border-brand hover:text-brand transition-all"
                            >
                                <Upload className="w-6 h-6" />
                                <span className="text-sm font-bold">Choose an image (PNG or JPEG)</span>
                            </button>

                            <p className="mt-3 flex items-start gap-1.5 text-xs text-gray-400 leading-relaxed">
                                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                The white background is removed automatically so your signature blends into the document.
                            </p>

                            {processing && (
                                <div className="mt-4 flex items-center justify-center gap-2 text-gray-400 py-6">
                                    <Loader2 className="w-5 h-5 animate-spin" /> Processing…
                                </div>
                            )}

                            {uploadError && (
                                <p className="mt-4 text-sm text-brand font-medium">{uploadError}</p>
                            )}

                            {uploadData && !processing && (
                                <div className="mt-4">
                                    <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Preview</p>
                                    <div
                                        className="rounded-2xl border border-gray-200 p-4 flex items-center justify-center"
                                        style={{
                                            backgroundImage:
                                                'linear-gradient(45deg,#f3f4f6 25%,transparent 25%),linear-gradient(-45deg,#f3f4f6 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#f3f4f6 75%),linear-gradient(-45deg,transparent 75%,#f3f4f6 75%)',
                                            backgroundSize: '16px 16px',
                                            backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
                                        }}
                                    >
                                        <img src={uploadData} alt="Signature preview" className="max-h-32 object-contain" />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 p-6 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 rounded-xl font-bold border border-gray-300 text-gray-600 hover:bg-gray-100 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!currentData || processing}
                        className="inline-flex items-center gap-2 bg-brand text-white px-6 py-2.5 rounded-xl font-bold hover:bg-[#c42e40] shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Check className="w-5 h-5" /> Use this signature
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SignatureModal;
